import React, { useCallback, useId } from 'react';
import { useIntl } from 'react-intl';
import { ASSET_INSTRUCTIONS_MAX_LENGTH, normaliseAssetInstructions } from 'fym-shared/src/music24/assetInstructions';
import { TypedMemo } from '../../../../common/types';
import { studioMessages } from '../studioMessages';
import { DetailField } from './DetailField';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface InstructionsFieldProps {
  /** The text in the field: the edited text, else the default. */
  value: string;
  /** The default text of the asset type in the theme and the style of the song. */
  defaultValue: string;
  onChange: (value: string) => void;
  /** Brings the default text back. */
  onReset: () => void;
}

/**
 * The Instructions of an asset (step 3, every asset type and the AI reel): a multi-line field that
 * opens with the default text that the director would use. The user edits it freely. "Reset to
 * default" shows when the text differs from the default. The hint says that the theme still applies.
 */
const InstructionsFieldComponent: React.FC<InstructionsFieldProps> = ({ value, defaultValue, onChange, onReset }) => {
  const intl = useIntl();
  const id = useId();
  const title = intl.formatMessage(studioMessages.instructionsTitle);
  const isEdited = normaliseAssetInstructions(value) !== normaliseAssetInstructions(defaultValue);
  const change = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value),
    [onChange]
  );

  return (
    <DetailField title={title} hint={intl.formatMessage(studioMessages.instructionsHint)}>
      <textarea
        id={id}
        className={styles.instructions}
        rows={4}
        value={value}
        maxLength={ASSET_INSTRUCTIONS_MAX_LENGTH}
        aria-label={title}
        aria-describedby={`${id}-note`}
        onChange={change}
      />
      <div className={styles.instructionsFoot}>
        <span id={`${id}-note`} className="small text-muted">
          {intl.formatMessage(studioMessages.instructionsNote)}
        </span>
        <span className={styles.instructionsCount}>
          {intl.formatMessage(studioMessages.instructionsCount, {
            count: value.length,
            max: ASSET_INSTRUCTIONS_MAX_LENGTH,
          })}
        </span>
      </div>
      {isEdited && (
        <div>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onReset}>
            {intl.formatMessage(studioMessages.instructionsReset)}
          </button>
        </div>
      )}
    </DetailField>
  );
};

export const InstructionsField = TypedMemo(InstructionsFieldComponent);
